import { gql } from 'apollo-angular';
import { Injectable } from '@angular/core';

import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ListFilter } from '../interfaces/list-filter.interface';
import { ListResponse } from '../interfaces/list-response.interface';
import { ResponseProperties } from '../interfaces/response-properties.interface';
import { ListRequest } from '../models/list-request.model';
import { GraphqlService } from './graphql.service';

@Injectable({
	providedIn: 'root'
})
export class ListService extends GraphqlService {
	/**
	 * Get a list from API
	 *
	 * @param list String of query name or a ListRequest object for special handling
	 * @param metaData Whether list elements directly integrated or in list property with other properties (e.g. totalCount)
	 * @param convertQuery Whether the query should be automatically converted to the default format
	 */
	getList(list: string | ListRequest, metaData: boolean = false, convertQuery: boolean = true): Observable<ListResponse | any[]> {
		return this.getLists([list], metaData, convertQuery).pipe(
			map((response) => {
				if (typeof list === 'string') {
					return response[list];
				} else {
					return response[list.id];
				}
			})
		);
	}

	/**
	 * Get lists from API
	 *
	 * @param lists Array with strings of query names or ListRequest objects for special handling
	 * @param metaData Whether list elements directly integrated or in list property with other properties (e.g. totalCount)
	 * @param convertQuery Whether the query should be automatically converted to the default format
	 */
	getLists(
		lists: (string | ListRequest)[],
		metaData: boolean = false,
		convertQuery: boolean = true
	): Observable<{ [id: string]: ListResponse } | { [id: string]: any[] }> {
		return new Observable<{ [id: string]: ListResponse } | { [id: string]: any[] }>((subscriber) => {
			// Init
			const response: { [id: string]: ListResponse } = {};
			const requests: Observable<object>[] = [];
			const filterParameters = {
				filters: '[WhereFilter]',
				pageIndex: 'Int',
				resultsPerPage: 'Int',
				orderBy: 'String',
				isAscending: 'String'
			};

			// Process lists
			lists.forEach((item) => {
				// Init
				let id: string;
				const filter: ListFilter = {};
				let properties: (string | ResponseProperties)[] = ['displayName', 'name', 'uid'];
				let query: string;

				// Configuration
				if (typeof item === 'string') {
					id = item;
					query = item;
				} else {
					id = item.id;
					Object.assign(filter, item.filter);
					properties = item.properties;
					query = item.query ? item.query : item.id;
				}

				// Convert query
				if (convertQuery) {
					query = query.toLowerCase();
					if (!query.endsWith('list')) {
						query += 'list';
					}
				}

				// Create qql string
				let gqlString = 'query ListQuery';
				let separator = '';

				// Outer parameters
				if (filter && Object.getOwnPropertyNames(filter).length) {
					gqlString += '(';

					for (const [param, type] of Object.entries(filterParameters)) {
						if (filter[param] !== undefined && filter[param] !== null) {
							gqlString += separator + '$' + param + ': ' + type;
							separator = ', ';
						}
					}

					gqlString += ')';
				}

				// Query
				gqlString += ` {
					${query}`;

				// Inner parameters
				if (filter && Object.getOwnPropertyNames(filter).length) {
					gqlString += '(';

					separator = '';
					for (const [param] of Object.entries(filterParameters)) {
						if (filter[param] !== undefined && filter[param] !== null) {
							gqlString += separator + param + ': $' + param;
							separator = ', ';
						}
					}

					gqlString += ')';
				}

				// Properties
				gqlString += ` {
						list {
							${ListRequest.getPropertyString(properties)}
						}
						totalCount
					}
				}
				`;

				// Create request
				requests.push(
					this.query(gql(gqlString), filter).pipe(
						map((listResponse) => {
							// Data with meta data
							let data = listResponse[query];

							// Data without meta data
							if (!metaData) {
								data = listResponse[query].list;
							}

							// Add list response data
							return (response[id] = data);
						})
					)
				);
			});

			// Start requests
			forkJoin(requests).subscribe(
				() => {
					// Return prepared response
					subscriber.next(response);
					subscriber.complete();
				},
				(error) => {
					// Throw error
					subscriber.error(error);
				}
			);
		});
	}

	getListsInOneQuery(lists: (string | ListRequest)[]): Observable<{ [id: string]: ListResponse }> {
		const response: { [id: string]: ListResponse } = {};
		// Create qql string
		let gqlString = 'query ListQuery {';

		// Process lists
		lists.forEach((item) => {
			// Init
			let properties: (string | ResponseProperties)[] = ['displayName', 'name', 'uid'];
			let query: string;

			// Configuration
			if (typeof item === 'string') {
				query = item;
			} else {
				properties = item.properties;
				query = item.query ? item.query : item.id;
			}

			if (!Array.isArray(properties) || properties.length === 0) {
				return;
			}

			// Query
			gqlString += `
			${query} {`;

			// Properties
			gqlString += `
						list {
							${ListRequest.getPropertyString(properties)}
						}
					}
				`;
		});
		// Closing Query
		gqlString += `
		}`;

		return this.query(gql(gqlString)).pipe(
			map((listResponse) => {
				// Add list response data

				lists.forEach((item) => {
					// Init
					let id: string;
					let query: string;

					// Configuration
					if (typeof item === 'string') {
						id = item;
						query = item;
					} else {
						id = item.id;
						query = item.query ? item.query : item.id;
					}

					response[id] = listResponse[query] ? listResponse[query].list : [];
				});
				return response;
			})
		);
	}
}
